import CampoTexto from '@/components/base/CampoTexto';
import CampoArea from '@/components/base/CampoArea';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import SeletorValor from '@/pages/assistente-evento/components/SeletorValor';
import type { Ingresso } from '../types';

interface CartaoIngressoProps {
  ingresso: Ingresso;
  indice: number;
  expandido: boolean;
  resumoVenda: string;
  aoAlternar: () => void;
  aoMudar: (patch: Partial<Ingresso>) => void;
  aoRemover: () => void;
}

export default function CartaoIngresso({
  ingresso: p,
  indice,
  expandido,
  resumoVenda,
  aoAlternar,
  aoMudar,
  aoRemover,
}: CartaoIngressoProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-secondary-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-secondary-100 px-4 py-2.5">
        <span className="flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
            {indice + 1}
          </span>
          <span className="text-sm font-medium text-foreground-900">Lote {indice + 1}</span>
        </span>
        <button
          type="button"
          onClick={aoRemover}
          aria-label="Apagar este lote"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-danger-50 hover:text-danger-600"
        >
          <i className="ri-close-line text-lg" />
        </button>
      </div>

      <div className="space-y-5 px-4 py-5">
        <CampoTexto
          id={`ingresso-nome-${indice}`}
          name="nome"
          label="Nome"
          help="Se quiser, renomeie. Ex.: Meia, Mesa para 4."
          value={p.nome}
          onChange={(v) => aoMudar({ nome: v })}
          required
          placeholder="1º lote"
        />

        <SeletorValor
          modo={p.valorModo}
          onChange={(valorModo) => aoMudar({ valorModo })}
          label="Preço do ingresso"
          notaADefinir="O ingresso aparece na página do evento, mas ninguém consegue comprar até você informar o valor."
        />
        {p.valorModo === 'pago' && (
          <div className="max-w-[320px]">
            <CampoDinheiro
              id={`ingresso-valor-${indice}`}
              name="valor"
              label="Preço"
              help="Quanto custa este ingresso."
              centavos={p.valorCentavos}
              onChange={(valorCentavos) => aoMudar({ valorCentavos })}
              required
            />
          </div>
        )}

        <div className="max-w-[320px]">
          <CampoTexto
            id={`ingresso-vagas-${indice}`}
            name="quantidade"
            label="Quantidade"
            help="Quantos ingressos existem. Deixe vazio se não tiver limite."
            value={p.vagas === null ? '' : String(p.vagas)}
            onChange={(v) =>
              aoMudar({
                vagas: v === '' ? null : Math.max(0, parseInt(v, 10) || 0),
                temLimite: v !== '',
              })
            }
            inputMode="numeric"
            placeholder="200"
          />
        </div>

        <div>
          <span className="block text-base font-medium text-foreground-950">À venda até</span>
          <p className="mt-1 text-sm text-secondary-500">A data que fecha este lote.</p>
          <div className="mt-2 flex gap-2">
            <input
              type="date"
              value={p.aVendaAteData}
              onChange={(e) => aoMudar({ aVendaAteData: e.target.value })}
              className="w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
            />
            <input
              type="time"
              value={p.aVendaAteHora}
              onChange={(e) => aoMudar({ aVendaAteHora: e.target.value })}
              className="w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
            />
          </div>
          <p className="mt-2 text-sm text-secondary-500">{resumoVenda}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={aoAlternar}
        className="flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap border-t border-secondary-100 px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-50 hover:text-foreground-800"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className={expandido ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </span>
        {expandido ? 'Menos opções' : 'Mais opções'}
      </button>

      {expandido && (
        <div className="space-y-5 border-t border-secondary-100 px-4 py-5">
          <div>
            <span className="block text-base font-medium text-foreground-950">À venda a partir de</span>
            <p className="mt-1 text-sm text-secondary-500">
              Normalmente a gente calcula sozinho. Só mexa se quiser uma data diferente.
            </p>
            <div className="mt-2 flex gap-2">
              <input
                type="date"
                value={p.aVendaDeData}
                onChange={(e) => aoMudar({ aVendaDeData: e.target.value })}
                className="w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
              />
              <input
                type="time"
                value={p.aVendaDeHora}
                onChange={(e) => aoMudar({ aVendaDeHora: e.target.value })}
                className="w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500"
              />
            </div>
          </div>

          <div className="max-w-[320px]">
            <CampoTexto
              id={`ingresso-max-compra-${indice}`}
              name="max_por_compra"
              label="Máximo por compra"
              help="Quantos ingressos a pessoa pode comprar de uma vez."
              value={String(p.maxPorCompra)}
              onChange={(v) =>
                aoMudar({ maxPorCompra: v === '' ? 1 : Math.max(1, parseInt(v, 10) || 1) })
              }
              inputMode="numeric"
              placeholder="10"
            />
          </div>

          <CampoArea
            id={`ingresso-desc-${indice}`}
            name="descricao"
            label="Descrição"
            help="Explique para quem é este ingresso. Ex.: estudante, com carteirinha."
            value={p.descricao}
            onChange={(v) => aoMudar({ descricao: v })}
            placeholder="estudante, com carteirinha"
          />
        </div>
      )}
    </div>
  );
}